import React, { useEffect, useMemo, useRef, useState } from 'react';
import Map, { Layer, Marker, Source } from 'react-map-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { Box, CircularProgress, IconButton, InputAdornment, Paper, TextField, Tooltip, makeStyles } from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import ZoomOutMapIcon from '@material-ui/icons/ZoomOutMap';
import { useTranslate } from 'react-admin';
import franceDepartments from './franceDepartments.json';
import { addDepartmentCode, formatDepartment } from './departments';
import { IgnGeocodeFeature, departmentCodeFromRaw, searchIgnAddresses, toggleDepartmentCode } from './ignGeocode';

const FRANCE_VIEW = { longitude: 2.4, latitude: 46.5, zoom: 4.6 };
const FILL_LAYER_ID = 'prestataire-departments-fill';
const ACTIVE_COLOR = '#1d6fb8';
const OTHER_COLOR = '#9ec3e6';
const EMPTY_COLOR = '#eef2f6';

export interface MapMarker {
    longitude: number;
    latitude: number;
}

const useStyles = makeStyles({
    mapWrap: {
        position: 'relative',
        height: 420,
        width: '100%',
        border: '1px solid #d0d7de',
        borderRadius: 8,
        overflow: 'hidden',
    },
    mapError: {
        borderColor: '#d32f2f',
    },
    suggestions: {
        position: 'absolute',
        zIndex: 20,
        left: 0,
        right: 0,
        marginTop: 4,
        maxHeight: 220,
        overflowY: 'auto',
    },
    suggestion: {
        display: 'block',
        width: '100%',
        textAlign: 'left',
        padding: '8px 12px',
        border: 0,
        background: 'transparent',
        cursor: 'pointer',
        fontSize: 14,
        '&:hover, &:focus': {
            background: '#eef4fb',
            outline: 'none',
        },
    },
    tooltip: {
        position: 'absolute',
        zIndex: 10,
        pointerEvents: 'none',
        padding: '2px 8px',
        fontSize: 12,
        transform: 'translate(12px, -50%)',
        whiteSpace: 'nowrap',
    },
    legend: {
        position: 'absolute',
        left: 8,
        bottom: 8,
        zIndex: 5,
        padding: '6px 10px',
        fontSize: 12,
        display: 'flex',
        flexDirection: 'column',
        gap: 4,
    },
    swatch: {
        display: 'inline-block',
        width: 12,
        height: 12,
        borderRadius: 2,
        marginRight: 6,
        verticalAlign: 'middle',
        border: '1px solid #5b7388',
    },
    recenter: {
        position: 'absolute',
        top: 8,
        right: 8,
        zIndex: 5,
        background: '#fff',
        '&:hover': { background: '#f3f6f9' },
    },
});

interface DepartmentMapPickerProps {
    /** Departments of the type being edited. */
    departmentCodes: number[];
    /** Departments already chosen for the other types, shown lighter for context. */
    otherDepartmentCodes?: number[];
    onChange: (codes: number[]) => void;
    /** Company location (geocoded address). */
    marker?: MapMarker | null;
    initialAddress?: string;
    /** Name of the type being edited, used in the legend. */
    activeLabel?: string;
    disabled?: boolean;
    error?: boolean;
}

const DepartmentMapPicker: React.FC<DepartmentMapPickerProps> = ({
    departmentCodes,
    otherDepartmentCodes = [],
    onChange,
    marker: companyMarker,
    initialAddress,
    activeLabel,
    disabled,
    error,
}) => {
    const classes = useStyles();
    const translate = useTranslate();
    const mapRef = useRef<any>(null);
    const [query, setQuery] = useState('');
    const [suggestions, setSuggestions] = useState<IgnGeocodeFeature[]>([]);
    const [searching, setSearching] = useState(false);
    const [marker, setMarker] = useState<MapMarker | null>(companyMarker || null);
    const [hovered, setHovered] = useState<{ code: number; x: number; y: number } | null>(null);

    const geojson = useMemo(() => {
        const collection = franceDepartments as { type: string; features: any[] };
        return {
            ...collection,
            features: (collection.features || []).map((feature) => ({
                ...feature,
                properties: {
                    ...feature.properties,
                    codeInt: departmentCodeFromRaw(String(feature.properties?.code || '')),
                },
            })),
        };
    }, []);

    const getMap = () => mapRef.current?.getMap?.() || mapRef.current;

    const flyTo = (target: MapMarker) => {
        getMap()?.flyTo?.({ center: [target.longitude, target.latitude], zoom: 6.5, duration: 800 });
    };

    const recenter = () => {
        getMap()?.flyTo?.({ center: [FRANCE_VIEW.longitude, FRANCE_VIEW.latitude], zoom: FRANCE_VIEW.zoom });
    };

    useEffect(() => {
        if (companyMarker) {
            setMarker(companyMarker);
        }
    }, [companyMarker]);

    useEffect(() => {
        const q = query.trim();
        if (q.length < 3 || disabled) {
            setSuggestions([]);
            setSearching(false);
            return undefined;
        }
        setSearching(true);
        const timer = window.setTimeout(() => {
            searchIgnAddresses(q)
                .then((results) => setSuggestions(results))
                .catch(() => setSuggestions([]))
                .finally(() => setSearching(false));
        }, 300);
        return () => window.clearTimeout(timer);
    }, [disabled, query]);

    const applyAddress = (feature: IgnGeocodeFeature) => {
        setQuery(feature.label);
        setSuggestions([]);
        const target = { longitude: feature.longitude, latitude: feature.latitude };
        setMarker(target);
        flyTo(target);
        if (feature.departmentCode) {
            onChange(addDepartmentCode(departmentCodes, feature.departmentCode));
        }
    };

    const fillPaint = useMemo(
        () => ({
            'fill-color': [
                'case',
                ['in', ['get', 'codeInt'], ['literal', departmentCodes.length ? departmentCodes : [-1]]],
                ACTIVE_COLOR,
                ['in', ['get', 'codeInt'], ['literal', otherDepartmentCodes.length ? otherDepartmentCodes : [-1]]],
                OTHER_COLOR,
                EMPTY_COLOR,
            ],
            'fill-opacity': 0.75,
        }),
        [departmentCodes, otherDepartmentCodes],
    );

    const codeFromEvent = (event: any): number | null => {
        const feature = event.features && event.features[0];
        return departmentCodeFromRaw(String(feature?.properties?.code || feature?.properties?.codeInt || ''));
    };

    const onMapClick = (event: any) => {
        if (disabled) {
            return;
        }
        const code = codeFromEvent(event);
        if (code) {
            onChange(toggleDepartmentCode(departmentCodes, code));
        }
    };

    const onMapMouseMove = (event: any) => {
        const code = codeFromEvent(event);
        setHovered(code ? { code, x: event.point?.x || 0, y: event.point?.y || 0 } : null);
    };

    return (
        <Box>
            <Box position='relative' marginBottom={1}>
                <TextField
                    fullWidth
                    variant='outlined'
                    size='small'
                    disabled={disabled}
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                    placeholder={initialAddress || translate('app.prestataires.fields.addressSearch')}
                    inputProps={{ 'aria-label': translate('app.prestataires.fields.addressSearch') }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position='start'>
                                <SearchIcon fontSize='small' color='action' />
                            </InputAdornment>
                        ),
                        endAdornment: searching ? <CircularProgress size={16} /> : null,
                    }}
                />
                {suggestions.length > 0 && (
                    <Paper className={classes.suggestions} elevation={3}>
                        {suggestions.map((item) => (
                            <button
                                key={`${item.label}-${item.longitude}`}
                                type='button'
                                className={classes.suggestion}
                                onClick={() => applyAddress(item)}
                            >
                                {item.label}
                            </button>
                        ))}
                    </Paper>
                )}
            </Box>
            <Box className={`${classes.mapWrap} ${error ? classes.mapError : ''}`}>
                <Map
                    ref={mapRef}
                    initialViewState={FRANCE_VIEW}
                    mapStyle={process.env.REACT_APP_MAP_STYLE || 'mapbox://styles/mapbox/light-v10'}
                    mapboxAccessToken={process.env.REACT_APP_MAPBOX_TOKEN || ''}
                    interactiveLayerIds={[FILL_LAYER_ID]}
                    onClick={onMapClick}
                    onMouseMove={onMapMouseMove}
                    onMouseLeave={() => setHovered(null)}
                    style={{ width: '100%', height: '100%' }}
                    cursor={disabled ? 'default' : 'pointer'}
                >
                    <Source id='prestataire-departments' type='geojson' data={geojson as any}>
                        <Layer id={FILL_LAYER_ID} type='fill' paint={fillPaint as any} />
                        <Layer
                            id='prestataire-departments-line'
                            type='line'
                            paint={{ 'line-color': '#5b7388', 'line-width': 0.6 }}
                        />
                        <Layer
                            id='prestataire-departments-hover'
                            type='line'
                            filter={['==', ['get', 'codeInt'], hovered ? hovered.code : -1] as any}
                            paint={{ 'line-color': '#0b3d6b', 'line-width': 2.5 }}
                        />
                    </Source>
                    {marker && <Marker longitude={marker.longitude} latitude={marker.latitude} color='#c0392b' />}
                </Map>
                {hovered && (
                    <Paper className={classes.tooltip} style={{ left: hovered.x, top: hovered.y }} elevation={2}>
                        {formatDepartment(hovered.code)}
                        {departmentCodes.includes(hovered.code) ? ' ✓' : ''}
                    </Paper>
                )}
                <Tooltip title={translate('app.prestataires.zone.recenter')}>
                    <IconButton size='small' className={classes.recenter} onClick={recenter}>
                        <ZoomOutMapIcon fontSize='small' />
                    </IconButton>
                </Tooltip>
                <Paper className={classes.legend} elevation={1}>
                    <span>
                        <span className={classes.swatch} style={{ background: ACTIVE_COLOR }} />
                        {activeLabel || translate('app.prestataires.zone.legendActive')}
                    </span>
                    {otherDepartmentCodes.length > 0 && (
                        <span>
                            <span className={classes.swatch} style={{ background: OTHER_COLOR }} />
                            {translate('app.prestataires.zone.legendOther')}
                        </span>
                    )}
                </Paper>
            </Box>
        </Box>
    );
};

export default DepartmentMapPicker;
